/* eslint-disable react/no-unescaped-entities */
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { PageHero } from '../components/SectionHeading';
import { ScrollReveal } from '../components/Motion';
import { ASSETS, PROJECTS, pick } from '../data/content';
import { useLang } from '../i18n/LanguageContext';

const REVENUE = [
    { year: 'Year 1', 'Direct Gas Sales': 33.7, 'Termobarrancas Power': 56.1, 'Crude Oil Sales': 67.2, 'Refinery + LNG': 0 },
    { year: 'Year 2', 'Direct Gas Sales': 138, 'Termobarrancas Power': 140.2, 'Crude Oil Sales': 296.6, 'Refinery + LNG': 0 },
    { year: 'Year 3', 'Direct Gas Sales': 128.1, 'Termobarrancas Power': 315.4, 'Crude Oil Sales': 830.4, 'Refinery + LNG': 15 },
    { year: 'Year 4', 'Direct Gas Sales': 128.1, 'Termobarrancas Power': 315.4, 'Crude Oil Sales': 1186.3, 'Refinery + LNG': 181.8 },
];

const COLORS = { 'Direct Gas Sales': '#E0C589', 'Termobarrancas Power': '#C9A961', 'Crude Oil Sales': '#11305f', 'Refinery + LNG': '#A38542' };

const TEXTS = {
    en: {
        centgaz_eye: 'Project · CENTGAZ',
        centgaz_title: 'CENTGAZ: The Corridor of Prosperity',
        centgaz_sub: 'Afghanistan Gas Pipeline Network — Integrated Export Corridor',
        centgaz_body: 'The CENTGAZ Project is a strategic cross-border gas transmission infrastructure project — establishing a direct gas transit corridor from Iran and Central Asian supply sources through Afghanistan to the Pakistani border, positioning Afghanistan as an indispensable energy transit state.',
        centgaz_stats: [['Herat', 'Northern entry hub'], ['Helmand', 'Mid-route node'], ['Kandahar', 'Southern hub'], ['Spin Boldak', 'AF–PK cross-border']],
        centgaz_bullets: ["Direct supply corridor to Pakistan's gas-deficit market", "Physical backbone for Afghanistan's national gas pipeline network", 'Enables LNG conversion and export via Gwadar terminal', 'Generates long-term transit revenue for the Afghan government'],
        seca_eye: 'Project · SECA Afghanistan',
        seca_title: 'Afghanistan Integrated Gas Development Program',
        seca_body: 'Amu Darya Basin — 30-Year Strategic Energy Cooperation Agreement. A multi-billion-dollar integrated program spanning upstream, midstream, and downstream — designed to transform Afghanistan into a Regional Gas Hub for Central Asia.',
        seca_phases: [
            { label: 'Upstream', amount: 'USD ~3 Bn', period: '20 years', body: 'Amu Darya, Herat, Afghan-Tajik, Katawaz Basins. Resource base ~16 TCF natural gas across four major basins.' },
            { label: 'Midstream', amount: 'USD 13–18 Bn', period: 'Total CAPEX', body: '3 major export corridors + 4 domestic corridors + Gwadar LNG Terminal + Compression & Storage.' },
            { label: 'Downstream', amount: '900 MW', period: '3 × 300 MW CCGT', body: 'Phased gas power cluster — Years 1–5. Projected Equity IRR 25–30% per phase.' },
        ],
        seca_kpis: [['16 TCF', 'Natural Gas'], ['1.6 Bn bbl', 'Crude Oil'], ['500 MMbbl', 'Natural Gas Liquids'], ['USD 1.5–2.5/MMBtu', 'Pipeline Transit Tariff']],
        barrancas_eye: 'Project · Venezuela',
        barrancas_title: 'Barrancas Area Integrated Gas Project',
        barrancas_sub: 'Barinas-Apure Basin, Venezuela — 35-year PDVSA Gas License. Built around 2.3 TCF of recoverable gas across three confirmed prospects, with four compounding revenue layers and projected IRR of 74.6%.',
        barr_layers: [
            ['04', 'LNG Export', 'USD 122M'],
            ['03', 'Topping Refinery (50,000 BPD)', 'USD 2,380M'],
            ['02', 'Termobarrancas Power (450 MW)', 'USD 827M'],
            ['01', '2.3 TCF Recoverable Gas', 'USD 428M'],
        ],
        barr_total_label: '4-Year Gross Total',
        barr_chart_label: 'Annual Revenue by Stream (USD M)',
        barr_kpis: [['74.6%', 'Project IRR'], ['7.4x', 'Equity Multiple'], ['~25 mo', 'Payback'], ['USD 351M', '4-Year Net Cash']],
        ug_eye: 'Project · Uganda 5BB',
        ug_title: 'Uganda 5BB Project',
        ug_sub: 'Southern Albertine Graben — East Africa Basin',
        ug_body: "Uganda's Albertine Graben has yielded 6.5 billion barrels of discovered oil, attracted USD 15 billion in infrastructure investment from TotalEnergies and CNOOC, and achieved an 88% all-in drilling success rate. Delta holds two exploration licenses: Kanywataba (KCA) and Turaco (TCA).",
        ug_stats: [['Discovered', '6.5 Bn bbl'], ['Success Rate', '58%'], ['All-in Drilling', '88%'], ['EACOP Export', '216–246k BPD']],
        ug_bullets: ['Kanywataba (KCA): P10 1,652 MMBBL · Drill-ready 2-well program', 'Turaco (TCA): P10 1,502 MMBBL · 5 prospects and leads', 'Total unrisked recoverable: 1,000 MMBBL (P10)', 'Projected IRR at $80/bbl: 24–30%'],
        ill_eye: 'Project · Illizi South',
        ill_title: 'Illizi South Exploration & Development',
        ill_sub: 'Algeria · Midad Energy / Sonatrach',
        ill_body: 'On October 13, 2025, Midad Energy North Africa — an affiliate of Delta International — signed a landmark 30-year PSA with Sonatrach for the Illizi South contractual area, ~100 km south of In Amenas in the Illizi Basin. The signing ceremony was attended by the Algerian Minister of Hydrocarbons and the Saudi Ambassador to Algeria.',
        ill_table: [['Block', 'Illizi South — ~100 km south of In Amenas'], ['Contract Type', 'PSA — Algerian Law No. 19-13'], ['Duration', '30 years + 10-year extension'], ['Total Investment', '~USD 5.4 Billion (100% Midad)'], ['Exploration Budget', 'USD 288 Million'], ['Expected Production', '993 MMboe over life'], ['Gas Production', '125 BCM natural gas'], ['Liquids', '204 MMbbl (103 LPG + 101 condensates)']],
        yan_eye: 'Project · Yanbu Delta Complex',
        yan_title: 'Yanbu Delta Complex',
        yan_sub: 'Red Sea · Saudi Arabia',
        yan_body: 'A landmark vision project of Delta International Energy — a large-scale, fully integrated downstream and midstream energy complex planned for development on the Red Sea coast of the Kingdom at the strategic industrial hub of Yanbu. Aligned with Vision 2030.',
        yan_zones: [
            { label: 'Zone 1 · Refinery', capacity: '50 MTPA', body: 'Processing Gulf crude into refined petroleum products for domestic Saudi demand and Red Sea export markets.' },
            { label: 'Zone 2 · LNG Terminal', capacity: '60 MTPA', body: 'Combined LNG import and export infrastructure enabling Saudi Arabia to participate in global LNG trade via the Red Sea corridor.' },
            { label: 'Zone 3 · Crude Storage', capacity: '10 MMbbl', body: 'Strategic crude storage for supply chain security and trading flexibility.' },
        ],
    },
    ar: {
        centgaz_eye: 'المشروع · سنتغاز',
        centgaz_title: 'سنتغاز: ممر الازدهار',
        centgaz_sub: 'شبكة أنابيب الغاز في أفغانستان — ممر التصدير المتكامل',
        centgaz_body: 'مشروع سنتغاز هو مشروع بنية تحتية استراتيجية لنقل الغاز عبر الحدود — يُنشئ ممر عبور غاز مباشر من مصادر إيران وآسيا الوسطى عبر أفغانستان إلى الحدود الباكستانية، مُموضعًا أفغانستان كدولة عبور طاقة لا غنى عنها.',
        centgaz_stats: [['هرات', 'محور الدخول الشمالي'], ['هلمند', 'عقدة منتصف المسار'], ['قندهار', 'المحور الجنوبي'], ['سبين بولداك', 'المعبر الأفغاني-الباكستاني']],
        centgaz_bullets: ['ممر إمداد مباشر لسوق الغاز ذات العجز في باكستان', 'العمود الفقري المادي للشبكة الوطنية لأنابيب الغاز في أفغانستان', 'يمكّن من تحويل الغاز إلى مسال وتصديره عبر محطة جوادر', 'يولّد إيرادات عبور طويلة الأمد للحكومة الأفغانية'],
        seca_eye: 'المشروع · SECA أفغانستان',
        seca_title: 'برنامج تطوير الغاز المتكامل في أفغانستان',
        seca_body: 'حوض أمو داريا — اتفاقية تعاون استراتيجي للطاقة لمدة 30 عامًا. برنامج متكامل بمليارات الدولارات يشمل القطاعات العلوي والأوسط والسفلي — مصمم لتحويل أفغانستان إلى مركز إقليمي للغاز في آسيا الوسطى.',
        seca_phases: [
            { label: 'القطاع العلوي', amount: '~3 مليار دولار', period: '20 سنة', body: 'أحواض أمو داريا، هرات، الأفغاني-الطاجيكي، كاتاواز. قاعدة موارد ~16 تريليون قدم مكعب من الغاز الطبيعي عبر أربعة أحواض رئيسية.' },
            { label: 'القطاع الأوسط', amount: '13–18 مليار دولار', period: 'إجمالي الإنفاق الرأسمالي', body: '3 ممرات تصدير رئيسية + 4 ممرات محلية + محطة جوادر للغاز المسال + الضغط والتخزين.' },
            { label: 'القطاع السفلي', amount: '900 ميغاواط', period: '3 × 300 ميغاواط CCGT', body: 'مجمع طاقة غازي متدرج — السنوات 1–5. عائد رأسمالي متوقع 25–30٪ لكل مرحلة.' },
        ],
        seca_kpis: [['16 تريليون قدم³', 'الغاز الطبيعي'], ['1.6 مليار برميل', 'النفط الخام'], ['500 مليون برميل', 'سوائل الغاز الطبيعي'], ['1.5–2.5 دولار/MMBtu', 'تعرفة عبور الأنابيب']],
        barrancas_eye: 'المشروع · فنزويلا',
        barrancas_title: 'مشروع غاز منطقة بارانكاس المتكامل',
        barrancas_sub: 'حوض بارينا-أبوري، فنزويلا — رخصة غاز PDVSA لمدة 35 عامًا. يُبنى حول 2.3 تريليون قدم مكعب من الغاز القابل للاستخراج عبر ثلاثة آفاق مؤكدة، مع أربع طبقات إيرادات متراكمة وIRR متوقع 74.6٪.',
        barr_layers: [
            ['04', 'تصدير الغاز المسال', '122 مليون دولار'],
            ['03', 'مصفاة طبقة علوية (50,000 برميل/يوم)', '2,380 مليون دولار'],
            ['02', 'طاقة تيرموبارانكاس (450 ميغاواط)', '827 مليون دولار'],
            ['01', '2.3 تريليون قدم مكعب من الغاز القابل للاستخراج', '428 مليون دولار'],
        ],
        barr_total_label: 'الإجمالي الإجمالي لأربع سنوات',
        barr_chart_label: 'الإيرادات السنوية حسب مصدر التدفق (مليون دولار)',
        barr_kpis: [['74.6٪', 'IRR المشروع'], ['7.4x', 'المضاعف الرأسمالي'], ['~25 شهرًا', 'فترة الاسترداد'], ['351 مليون دولار', 'صافي النقد لأربع سنوات']],
        ug_eye: 'المشروع · أوغندا 5BB',
        ug_title: 'مشروع أوغندا 5BB',
        ug_sub: 'وادي ألبرتاين الجنوبي — حوض شرق إفريقيا',
        ug_body: 'أنتج وادي ألبرتاين في أوغندا 6.5 مليار برميل من النفط المكتشف، واستقطب 15 مليار دولار من استثمارات البنية التحتية من TotalEnergies وCNOOC، وحقق معدل نجاح حفر إجمالي 88٪. تمتلك دلتا رخصتي استكشاف: كانيواتابا (KCA) وتوراكو (TCA).',
        ug_stats: [['المكتشف', '6.5 مليار برميل'], ['معدل النجاح', '58٪'], ['نجاح الحفر الإجمالي', '88٪'], ['تصدير EACOP', '216–246 ألف برميل/يوم']],
        ug_bullets: ['كانيواتابا (KCA): P10 1,652 مليون برميل · برنامج بئرين جاهز للحفر', 'توراكو (TCA): P10 1,502 مليون برميل · 5 آفاق وقيادات', 'الإجمالي القابل للاستخراج (غير مرجح): 1,000 مليون برميل (P10)', 'IRR المتوقع عند 80 دولار/برميل: 24–30٪'],
        ill_eye: 'المشروع · إليزي الجنوبي',
        ill_title: 'استكشاف وتطوير إليزي الجنوبي',
        ill_sub: 'الجزائر · مداد للطاقة / سوناطراك',
        ill_body: 'في 13 أكتوبر 2025، وقّعت شركة مداد للطاقة شمال إفريقيا — التابعة لدلتا الدولية — اتفاقية تقاسم إنتاج تاريخية لمدة 30 عامًا مع سوناطراك حول منطقة إليزي الجنوبي التعاقدية، على بعد ~100 كم جنوب عين أمناس في حوض إليزي. حضر حفل التوقيع وزير المحروقات الجزائري والسفير السعودي لدى الجزائر.',
        ill_table: [['القطعة', 'إليزي الجنوبي — ~100 كم جنوب عين أمناس'], ['نوع العقد', 'PSA — القانون الجزائري رقم 19-13'], ['المدة', '30 عامًا + تمديد 10 سنوات'], ['الاستثمار الإجمالي', '~5.4 مليار دولار (100٪ مداد)'], ['ميزانية الاستكشاف', '288 مليون دولار'], ['الإنتاج المتوقع', '993 مليون برميل مكافئ نفطي طوال العمر'], ['إنتاج الغاز', '125 مليار متر مكعب من الغاز الطبيعي'], ['السوائل', '204 مليون برميل (103 LPG + 101 مكثفات)']],
        yan_eye: 'المشروع · مجمع دلتا ينبع',
        yan_title: 'مجمع دلتا ينبع',
        yan_sub: 'البحر الأحمر · المملكة العربية السعودية',
        yan_body: 'مشروع رؤية تاريخي لدلتا الدولية للطاقة — مجمع طاقة متكامل ضخم للقطاعين السفلي والأوسط مُخطط له على ساحل البحر الأحمر في المملكة في المركز الصناعي الاستراتيجي بينبع. متوافق مع رؤية 2030.',
        yan_zones: [
            { label: 'المنطقة 1 · المصفاة', capacity: '50 مليون طن/سنة', body: 'معالجة الخام الخليجي إلى منتجات بترولية مكررة للطلب المحلي السعودي وأسواق التصدير عبر البحر الأحمر.' },
            { label: 'المنطقة 2 · محطة الغاز المسال', capacity: '60 مليون طن/سنة', body: 'بنية تحتية مشتركة لاستيراد وتصدير الغاز المسال تمكّن المملكة العربية السعودية من المشاركة في تجارة الغاز المسال العالمية عبر ممر البحر الأحمر.' },
            { label: 'المنطقة 3 · تخزين النفط الخام', capacity: '10 مليون برميل', body: 'تخزين استراتيجي للنفط الخام لأمن سلسلة التوريد ومرونة التداول.' },
        ],
    },
};

const Projects = () => {
    const { t, lang } = useLang();
    const tx = TEXTS[lang] || TEXTS.en;
    return (
        <div data-testid="projects-page">
            <PageHero
                eyebrow={t('projects.hero_eyebrow')}
                title={t('projects.hero_title')}
                sub={t('projects.hero_sub')}
                image={ASSETS.desertPipeline}
            />

            {/* Overview Table */}
            <section className="bg-cream py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="overflow-x-auto">
                            <table className="editorial-table w-full" data-testid="projects-overview">
                                <thead>
                                    <tr>
                                        <th>{t('projects.col_project')}</th>
                                        <th>{t('projects.col_geo')}</th>
                                        <th>{t('projects.col_comm')}</th>
                                        <th>{t('projects.col_stage')}</th>
                                        <th>{t('projects.col_role')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {PROJECTS.map((p) => (
                                        <tr key={p.slug} data-testid={`project-row-${p.slug}`}>
                                            <td className="font-display text-navy">{pick(p, 'name', lang)}</td>
                                            <td className="text-ink/70 whitespace-nowrap">{pick(p, 'country', lang)}</td>
                                            <td className="text-ink/70">{pick(p, 'commodity', lang)}</td>
                                            <td><span className="text-[10px] tracking-widest uppercase bg-navy text-gold px-2.5 py-1">{pick(p, 'stage', lang)}</span></td>
                                            <td className="text-ink/70">{pick(p, 'role', lang)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            {/* CENTGAZ */}
            <ProjectBlock
                eyebrow={tx.centgaz_eye}
                title={tx.centgaz_title}
                sub={tx.centgaz_sub}
                body={tx.centgaz_body}
                bg="white"
                stats={tx.centgaz_stats.map(([label, val]) => ({ label, val }))}
                bullets={tx.centgaz_bullets}
            />

            {/* SECA Afghanistan */}
            <section className="bg-navy-deep py-24 md:py-32 grain-overlay">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="max-w-3xl mb-16">
                            <div className="eyebrow mb-5">{tx.seca_eye}</div>
                            <h2 className="editorial-h2 text-white text-3xl md:text-5xl mb-6">
                                {tx.seca_title}
                            </h2>
                            <p className="text-white/70 leading-relaxed">
                                {tx.seca_body}
                            </p>
                        </div>
                    </ScrollReveal>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gold/20">
                        {tx.seca_phases.map((p) => (
                            <div key={p.label} className="bg-navy-deep p-10">
                                <div className="eyebrow mb-3">{p.label}</div>
                                <div className="metric-number text-3xl md:text-4xl">{p.amount}</div>
                                <div className="text-white/55 text-sm mt-1 mb-6">{p.period}</div>
                                <p className="text-white/70 text-sm leading-relaxed">{p.body}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-gold/15">
                        {tx.seca_kpis.map(([v, l]) => (
                            <div key={l} className="bg-navy-deep p-6 text-center">
                                <div className="metric-number text-xl md:text-2xl">{v}</div>
                                <div className="text-white/60 text-[11px] tracking-widest uppercase mt-2">{l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Barrancas */}
            <section className="bg-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="max-w-3xl mb-12">
                            <div className="eyebrow mb-5">{tx.barrancas_eye}</div>
                            <h2 className="editorial-h2 text-navy text-3xl md:text-5xl">
                                {tx.barrancas_title}
                            </h2>
                            <p className="mt-5 text-ink/70 leading-relaxed">
                                {tx.barrancas_sub}
                            </p>
                        </div>
                    </ScrollReveal>

                    {/* 4-layer value diagram */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
                        <ScrollReveal>
                            <div className="space-y-px bg-navy/20">
                                {tx.barr_layers.map(([n, name, amount], i) => {
                                    const bgs = ['bg-gold', 'bg-navy', 'bg-gold-deep', 'bg-navy-deep'];
                                    const bg = bgs[i];
                                    const isGold = bg === 'bg-gold';
                                    return (
                                        <div key={n} className={`${bg} p-6 flex items-center justify-between`}>
                                            <div>
                                                <div className={`text-[10px] tracking-widest uppercase ${isGold ? 'text-navy-deep/70' : 'text-gold'}`}>
                                                    {lang === 'ar' ? 'الطبقة' : 'Layer'} {n}
                                                </div>
                                                <div className={`font-display text-lg md:text-xl mt-1 ${isGold ? 'text-navy-deep' : 'text-white'}`}>
                                                    {name}
                                                </div>
                                            </div>
                                            <div className={`font-display text-xl md:text-2xl ${isGold ? 'text-navy-deep' : 'text-gold'}`}>
                                                {amount}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="bg-cream border border-navy/15 p-6 text-center">
                                    <div className="eyebrow mb-2">{tx.barr_total_label}</div>
                                    <div className="metric-number text-3xl">USD 3,474 M</div>
                                </div>
                            </div>
                        </ScrollReveal>

                        <ScrollReveal delay={0.1}>
                            <div className="bg-white border border-navy/10 p-6">
                                <div className="eyebrow mb-4">{tx.barr_chart_label}</div>
                                <div className="w-full h-72">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={REVENUE} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                                            <CartesianGrid strokeDasharray="2 4" stroke="rgba(11,30,63,0.08)" vertical={false} />
                                            <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#232529' }} axisLine={false} tickLine={false} />
                                            <YAxis tick={{ fontSize: 11, fill: '#232529' }} axisLine={false} tickLine={false} />
                                            <Tooltip contentStyle={{ background: '#0B1E3F', border: '1px solid rgba(201,169,97,0.4)', color: 'white' }} />
                                            <Legend wrapperStyle={{ fontSize: 11 }} />
                                            {Object.keys(COLORS).map((k) => (
                                                <Bar key={k} dataKey={k} stackId="a" fill={COLORS[k]} />
                                            ))}
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-navy/10 mt-6">
                                {tx.barr_kpis.map(([v, l]) => (
                                    <div key={l} className="bg-white p-5 text-center">
                                        <div className="metric-number text-xl md:text-2xl">{v}</div>
                                        <div className="text-ink/55 text-[10px] tracking-widest uppercase mt-1.5">{l}</div>
                                    </div>
                                ))}
                            </div>
                        </ScrollReveal>
                    </div>
                </div>
            </section>

            {/* Uganda */}
            <ProjectBlock
                eyebrow={tx.ug_eye}
                title={tx.ug_title}
                sub={tx.ug_sub}
                body={tx.ug_body}
                bg="white"
                stats={tx.ug_stats.map(([label, val]) => ({ label, val }))}
                bullets={tx.ug_bullets}
            />

            {/* Illizi South */}
            <section className="relative bg-navy-deep py-24 md:py-32 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://customer-assets.emergentagent.com/job_energy-innovation-sa/artifacts/sogsw8kw_ALGERIA%20PROJECT%201.png"
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-navy-deep/70" />
                    <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-navy-deep/90 via-navy-deep/60 to-transparent" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <ScrollReveal>
                            <div className="eyebrow mb-5">{tx.ill_eye}</div>
                            <h2 className="editorial-h2 text-white text-3xl md:text-4xl">
                                {tx.ill_title}
                            </h2>
                            <p className="mt-2 text-gold text-[12px] tracking-widest uppercase">{tx.ill_sub}</p>
                            <p className="mt-6 text-white/70 leading-relaxed">
                                {tx.ill_body}
                            </p>
                        </ScrollReveal>
                    </div>
                    <div className="lg:col-span-7">
                        <ScrollReveal delay={0.1}>
                            <table className="editorial-table editorial-table-dark w-full">
                                <tbody>
                                    {tx.ill_table.map(([k, v]) => (
                                        <tr key={k}>
                                            <td className="font-display text-gold w-1/3">{k}</td>
                                            <td>{v}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </ScrollReveal>
                    </div>
                </div>
            </section>

            {/* Yanbu */}
            <section className="bg-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <ScrollReveal>
                        <div className="max-w-3xl mb-12">
                            <div className="eyebrow mb-5">{tx.yan_eye}</div>
                            <h2 className="editorial-h2 text-navy text-3xl md:text-5xl">
                                {tx.yan_title}
                            </h2>
                            <p className="mt-2 text-gold text-[12px] tracking-widest uppercase">{tx.yan_sub}</p>
                            <p className="mt-6 text-ink/75 leading-relaxed">
                                {tx.yan_body}
                            </p>
                        </div>
                    </ScrollReveal>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-navy/15">
                        {tx.yan_zones.map((z) => (
                            <div key={z.label} className="bg-white p-8 hover:bg-cream transition-colors">
                                <div className="eyebrow mb-3">{z.label}</div>
                                <div className="metric-number text-3xl md:text-4xl">{z.capacity}</div>
                                <p className="text-ink/70 text-sm leading-relaxed mt-5">{z.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

const ProjectBlock = ({ eyebrow, title, sub, body, stats, bullets, bg = 'cream' }) => (
    <section className={`py-24 md:py-32 ${bg === 'white' ? 'bg-white' : 'bg-cream'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <ScrollReveal className="lg:col-span-5">
                <div className="eyebrow mb-5">{eyebrow}</div>
                <h2 className="editorial-h2 text-navy text-3xl md:text-4xl mb-3">{title}</h2>
                <p className="text-gold text-[12px] tracking-widest uppercase">{sub}</p>
                <p className="mt-6 text-ink/75 leading-relaxed">{body}</p>
            </ScrollReveal>
            <ScrollReveal delay={0.1} className="lg:col-span-7">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-navy/15 mb-8">
                    {stats.map((s) => (
                        <div key={s.label} className="bg-white p-5 text-center">
                            <div className="text-[10px] tracking-widest uppercase text-ink/55 mb-2">{s.label}</div>
                            <div className="font-display text-navy text-lg leading-tight">{s.val}</div>
                        </div>
                    ))}
                </div>
                <ul className="space-y-3">
                    {bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-3 text-ink/80 text-sm md:text-base">
                            <span className="metric-number text-base w-8 flex-shrink-0">{String(i + 1).padStart(2, '0')}</span>
                            <span>{b}</span>
                        </li>
                    ))}
                </ul>
            </ScrollReveal>
        </div>
    </section>
);

export default Projects;
